import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { HorizontalLine } from "@faire/web--source/slate/Layout";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { FaireMarketTheme } from "@faire/web--source/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { EventsThemeLogo } from "@faire/web--source/slate/Theme/EventsThemeLogo";
import { EventsThemeShape } from "@faire/web--source/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import marketsAllSpecials from "@faire/web-api--source/routes/www/markets/event_type/all-specials";
import React from "react";
import styled from "styled-components";

interface IProps {
  layoutElement: ILayoutElement;
  onClick?: () => void;
}

export const HomeFaireMarketHero: React.FC<IProps> = ({
  layoutElement,
  onClick,
}) => {
  const { isInsiderDay, currentEventType } = useMarketStore();
  const preamble = (
    <Typography
      data-test-id="market-hero-section-preamble"
      variant="subheadingSansMedium"
      color={layoutElement.tagline_text_color}
    >
      {layoutElement.tagline_text}
    </Typography>
  );

  const preambleIcon = isInsiderDay ? (
    <>
      <StyledInsider />
      <Spacer size="1x" />
    </>
  ) : null;

  const title = (
    <Typography
      data-test-id="market-hero-section-title"
      variant={{
        mobileAndAbove: "displaySSerifRegular",
        tabletAndAbove: "displayLSerifRegular",
      }}
      color={layoutElement.main_text_color}
    >
      {layoutElement.main_text}
    </Typography>
  );

  const subtitle = layoutElement.description_text ? (
    <Column>
      <Typography
        data-test-id="market-hero-section-subtitle"
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
        color={layoutElement.description_text_color}
      >
        {layoutElement.description_text}
      </Typography>
      <Spacer size="3x" />
    </Column>
  ) : null;

  const ctaButton = (
    <ThemeProvider theme={FaireMarketTheme}>
      <StyledButton
        data-test-id="market-hero-section-cta"
        linkTo={marketsAllSpecials(currentEventType)}
        onClick={onClick}
      >
        <StrictLocalMsg
          defaultMessage="Browse participating brands"
          description={{
            text: "Call to action to browse all participating brands",
          }}
        />
      </StyledButton>
    </ThemeProvider>
  );

  return (
    <Wrapper>
      <Container>
        <Content>
          <ResponsiveContentV2
            desktopAndAbove={<StyledMarketPrimaryLogo />}
            tabletAndBelow={<StyledFaireMarketIcon />}
          />
          <TopContent>
            <Row>
              {preambleIcon}
              {preamble}
            </Row>
            <Spacer size="3x" />
            {title}
          </TopContent>
          <Spacer size="3x" />
          {subtitle}
          {ctaButton}
        </Content>
        <Spacer size="5x" />
        <HorizontalLine color={getEventTheme("faireMarket").primaryLight} />
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background-color: ${getEventTheme("faireMarket").primaryDark};
`;

const Container = styled.div`
  max-width: 1280px;
  padding: ${getSpacing("7x", "6x", 0)};
  margin: 0 auto;

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("5x", "2x", 0)};
  }
`;

const Content = styled(Column)`
  position: relative;

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-right: 444px;
  }
`;

const TopContent = styled(Column)`
  ${responsiveMediaQueries.tabletAndBelow} {
    padding-right: ${getSpacing("10x")};
  }
`;

const StyledButton = styled(Button).attrs({
  variant: "secondary",
})`
  width: fit-content;
  border: 0;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const StyledMarketPrimaryLogo = styled(
  EventsThemeLogo.faireMarket.primary
).attrs({
  isInverse: true,
  width: "220px",
  height: "177px",
})`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledFaireMarketIcon = styled(EventsThemeShape.faireMarket.badge).attrs({
  width: "64px",
})`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInsider = styled(Insider).attrs({
  color: getEventTheme("faireMarket").promoTextInverse,
})`
  margin-top: 2px;
`;
